import * as React from 'react';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addYears,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfYear,
} from 'date-fns';

const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;
const NOW_REFRESH_MS = 60 * 1000;
// Below this a 5-minute bucket fills the graph; a narrower drag is widened to it.
const MIN_SPAN = 30 * 60 * 1000;

/** Nothing on the platform predates this; the backend refuses anything earlier. */
export const ANALYTICS_EARLIEST = new Date(2015, 0, 1).getTime();

export const ANALYTICS_PRESETS: { span: number; label: string }[] = [
  { span: DAY, label: '24h' },
  { span: 7 * DAY, label: '7d' },
  { span: 30 * DAY, label: '30d' },
  { span: 365 * DAY, label: '1y' },
];
const ZOOM_SPANS = [HOUR, 6 * HOUR, DAY, 7 * DAY, 30 * DAY, 90 * DAY, 365 * DAY, 2 * 365 * DAY, 5 * 365 * DAY];

export type QueryBucket = '5m' | '1d' | '1mo';
export type DisplayStep = '5m' | '1h' | '1d' | '1mo';

/** Which backend bucket a span is fetched in, and the step its bars are drawn in. The 5m data is
 *  summed to hours client-side so a week stays readable without its own backend query. */
export type Resolution = { bucket: QueryBucket; step: DisplayStep };

export function resolutionFor(span: number): Resolution {
  if (span <= 36 * HOUR) return { bucket: '5m', step: '5m' };
  if (span <= 14 * DAY) return { bucket: '5m', step: '1h' };
  if (span <= 400 * DAY) return { bucket: '1d', step: '1d' };
  return { bucket: '1mo', step: '1mo' };
}

export function stepStart(time: number, step: DisplayStep): number {
  switch (step) {
    case '5m':
      return time - (time % (5 * 60 * 1000));
    case '1h':
      return startOfHour(time).getTime();
    case '1d':
      return startOfDay(time).getTime();
    case '1mo':
      return startOfMonth(time).getTime();
  }
}

export function nextStep(time: number, step: DisplayStep): number {
  switch (step) {
    case '5m':
      return addMinutes(time, 5).getTime();
    case '1h':
      return addHours(time, 1).getTime();
    case '1d':
      return addDays(time, 1).getTime();
    case '1mo':
      return addMonths(time, 1).getTime();
  }
}

/** Widens a window to the whole days, months or years the backend caches in, so panning within
 *  them reuses the same request. The backend widens again in the site's own timezone. */
export function fetchRange(start: number, end: number, bucket: QueryBucket): { start: number; end: number } {
  const [floor, next] =
    bucket === '5m' ? [startOfDay, addDays] : bucket === '1d' ? [startOfMonth, addMonths] : [startOfYear, addYears];
  const alignedEnd = floor(end).getTime() === end ? end : next(floor(end), 1).getTime();
  return { start: floor(Math.max(start, ANALYTICS_EARLIEST)).getTime(), end: alignedEnd };
}

type WindowState = { span: number; end: number; followsNow: boolean };

export function useAnalyticsWindow(initialSpan = 7 * DAY) {
  const [state, setState] = React.useState<WindowState>({ span: initialSpan, end: Date.now(), followsNow: true });
  const [now, setNow] = React.useState(Date.now);

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), NOW_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const end = state.followsNow ? now : state.end;
  const span = state.span;
  const start = end - span;
  // A dragged selection can have any span, so zooming steps to the nearest preset size either way.
  const zoomInSpan = [...ZOOM_SPANS].reverse().find((zoom) => zoom < span);
  const zoomOutSpan = ZOOM_SPANS.find((zoom) => zoom > span);

  const setSpan = (next: number) => setState({ span: next, end: Date.now(), followsNow: true });

  const shift = (direction: -1 | 1) =>
    setState((current) => {
      const nextEnd = Math.min(end + direction * span, Date.now());
      return { ...current, end: nextEnd, followsNow: nextEnd >= Date.now() };
    });

  const zoomTo = (next: number | undefined) => {
    if (next === undefined) return;
    // Zooming keeps the window's end where it is, like the location timeline.
    setState((current) => ({ ...current, span: next, end }));
  };

  /** Zooms to an exact range, e.g. one dragged on a graph. A range up to now keeps following it. */
  const setRange = (rangeStart: number, rangeEnd: number) => {
    const clampedEnd = Math.min(rangeEnd, Date.now());
    const nextSpan = Math.max(MIN_SPAN, clampedEnd - rangeStart);
    setState({ span: nextSpan, end: clampedEnd, followsNow: clampedEnd >= Date.now() - NOW_REFRESH_MS });
  };

  return {
    start,
    end,
    span,
    preset: state.span,
    followsNow: state.followsNow,
    resolution: resolutionFor(span),
    setSpan,
    setRange,
    shift,
    canShiftEarlier: start > ANALYTICS_EARLIEST,
    canShiftLater: !state.followsNow,
    canZoomIn: zoomInSpan !== undefined,
    canZoomOut: zoomOutSpan !== undefined,
    zoomIn: () => zoomTo(zoomInSpan),
    zoomOut: () => zoomTo(zoomOutSpan),
  };
}

export type AnalyticsWindow = ReturnType<typeof useAnalyticsWindow>;
